// AufrufControlPage.jsx — Schreiber-Steuerung für Kämpfer-Aufruf
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './AufrufControlPage.css';

function getSlotStatusSimple(slot, turnierdatum) {
  if (!turnierdatum || !slot.start_time) return 'upcoming';
  const datePart = String(turnierdatum).slice(0, 10);
  const now = Date.now();
  const start = new Date(`${datePart}T${slot.start_time}:00`);
  if (now < start.getTime()) return 'upcoming';
  if (slot.end_time) {
    const end = new Date(`${datePart}T${slot.end_time}:00`);
    return now < end.getTime() ? 'active' : 'done';
  }
  return now < start.getTime() + 3600000 ? 'active' : 'done';
}

/**
 * Was der Hallenschirm (/aufruf/:turnierId) unten zeigt: Hinweise des
 * Veranstalters und das Einwaage-Zeitfenster (25.09.2026).
 *
 * Wird EINMAL geladen und nicht nachgepollt — sonst überschriebe der
 * 15-Sekunden-Takt, was gerade getippt wird.
 */
function HallenInfo({ turnierId }) {
  const [form, setForm] = useState({ hallen_info: '', einwaage_von: '', einwaage_bis: '' });
  const [geladen, setGeladen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState('');

  useEffect(() => {
    fetch(`/api/brackets/turnier/${turnierId}/hallen-info`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setForm({
          hallen_info: d.data.hallen_info || '',
          einwaage_von: d.data.einwaage_von || '',
          einwaage_bis: d.data.einwaage_bis || '',
        });
      })
      .catch(() => {})
      .finally(() => setGeladen(true));
  }, [turnierId]);

  async function speichern() {
    setLaeuft(true);
    setMeldung('');
    try {
      const r = await fetch(`/api/brackets/turnier/${turnierId}/hallen-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 401) setMeldung('Nicht angemeldet — bitte neu anmelden.');
      else if (r.status === 403) setMeldung(d.error || 'Dieser Zugang darf das nicht.');
      else if (!r.ok) setMeldung(d.error || 'Speichern fehlgeschlagen.');
      else setMeldung('Gespeichert — der Hallenschirm zeigt es mit der nächsten Aktualisierung (spätestens 20 s).');
    } catch {
      setMeldung('Server nicht erreichbar.');
    } finally {
      setLaeuft(false);
    }
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <details className="acp-halle">
      <summary>Hallenschirm: Hinweise und Einwaage</summary>
      {!geladen ? <div className="acp-halle-laedt">Lade …</div> : (
        <>
          <label className="acp-halle-feld">
            <span>Hinweise — eine Meldung je Zeile, sie wechseln alle 8 Sekunden</span>
            <textarea rows={4} maxLength={4000} value={form.hallen_info} onChange={set('hallen_info')}
              placeholder={'Mittagspause 12:30 bis 13:15\nSiegerehrung Kinder um 14:00 an Tatami 1\nSanitäter: Eingang Halle B'} />
          </label>
          <div className="acp-halle-zeile">
            <label className="acp-halle-feld">
              <span>Einwaage von</span>
              <input type="datetime-local" value={form.einwaage_von} onChange={set('einwaage_von')} />
            </label>
            <label className="acp-halle-feld">
              <span>bis</span>
              <input type="datetime-local" value={form.einwaage_bis} onChange={set('einwaage_bis')} />
            </label>
          </div>
          <div className="acp-halle-aktionen">
            <button onClick={speichern} disabled={laeuft}>{laeuft ? 'speichert …' : 'Speichern'}</button>
            <a href={`/aufruf/${turnierId}`} target="_blank" rel="noopener noreferrer">Hallenschirm öffnen</a>
          </div>
          {meldung && <div className="acp-durchsage-meldung">{meldung}</div>}
        </>
      )}
    </details>
  );
}

export default function AufrufControlPage() {
  const { turnierId } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeCall, setActiveCall] = useState(null); // aktuell aufgerufene Kategorie
  const [calling, setCalling]   = useState(null);     // welcher Button lädt gerade
  const [sperre, setSperre]     = useState('');       // Hinweis, wenn die Anmeldung fehlt
  const [durchsage, setDurchsage]             = useState('');
  const [durchsageLaeuft, setDurchsageLaeuft] = useState(false);
  const [durchsageMeldung, setDurchsageMeldung] = useState('');

  const load = useCallback(() => {
    fetch(`/api/brackets/turnier/${turnierId}/aufruf`)
      .then(r => r.json())
      .then(d => { if (d.success) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [turnierId]);

  const loadState = useCallback(() => {
    fetch(`/api/brackets/turnier/${turnierId}/aufruf-state`)
      .then(r => r.json())
      .then(d => setActiveCall(d.state || null))
      .catch(() => {});
  }, [turnierId]);

  useEffect(() => {
    load();
    loadState();
    const i1 = setInterval(load, 15000);
    const i2 = setInterval(loadState, 3000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [load, loadState]);

  // Freie Durchsage: geht in dieselbe Warteschlange wie die automatischen
  // Aufrufe und wird von der Ansage-Station gesprochen — mit Vorrang, weil sie
  // von einem Menschen kommt, der einen Grund hat.
  async function durchsageSenden() {
    const text = durchsage.trim();
    if (!text) return;
    setDurchsageLaeuft(true);
    setDurchsageMeldung('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`/api/ansagen/turnier/${turnierId}/frei`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const d = await r.json();
      if (d.success) {
        setDurchsage('');
        setDurchsageMeldung('In die Warteschlange gelegt — die Station spricht sie als Nächstes.');
      } else {
        setDurchsageMeldung(d.error || 'Durchsage konnte nicht gesendet werden');
      }
    } catch {
      setDurchsageMeldung('Server nicht erreichbar');
    } finally {
      setDurchsageLaeuft(false);
    }
  }

  // Die Steuerung verlangt seit 26.08.2026 eine Anmeldung als Turnier-Personal
  // (Anmeldung, Waage, Kampfrichter) oder als Verband. Der Token liegt nach dem
  // Login unter `token` — beide Personal-Anmeldungen legen ihn dort ab.
  //
  // Fehlt er oder passt er nicht zum Turnier, wird das angezeigt statt still
  // zu scheitern: am Wettkampftag muss sofort erkennbar sein, warum ein Aufruf
  // nicht auf der Matte ankommt.
  async function senden(nutzlast) {
    const token = localStorage.getItem('token');
    if (!token) {
      setSperre('Nicht angemeldet — bitte über /reg-login oder /staff-login anmelden.');
      return false;
    }
    try {
      const res = await fetch(`/api/brackets/turnier/${turnierId}/aufruf-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(nutzlast),
      });
      if (res.status === 401) { setSperre('Anmeldung abgelaufen — bitte neu anmelden.'); return false; }
      if (res.status === 403) { setSperre('Dieser Zugang gehört nicht zu diesem Turnier.'); return false; }
      if (!res.ok)            { setSperre('Der Aufruf konnte nicht gesendet werden.'); return false; }
      setSperre('');
      return true;
    } catch {
      setSperre('Keine Verbindung — der Aufruf kam nicht an.');
      return false;
    }
  }

  async function call(division_code, label, tatami) {
    setCalling(division_code);
    const ok = await senden({ division_code, label, tatami });
    if (ok) setActiveCall({ division_code, label, tatami });
    setCalling(null);
  }

  async function clearCall() {
    if (await senden({ division_code: null })) setActiveCall(null);
  }

  const turnierdatum = data?.turnier?.datum ?? null;

  // Alle aufzurufenden Elemente: ready Kämpfe + zeitplan Slots
  const items = (() => {
    if (!data) return [];
    const list = [];

    // Bereit-Kämpfe
    for (const k of data.ready) {
      list.push({
        type: 'kampf',
        division_code: k.division_code || k.bracket_name,
        label: k.bracket_name,
        tatami: k.tatami_nummer ?? k.matte_nummer,
        time: null,
        detail: `Runde ${k.runde} — ${k.k1_vorname ?? '?'} ${k.k1_nachname ?? ''} vs ${k.k2_vorname ?? '?'} ${k.k2_nachname ?? ''}`,
        sortKey: `${String(k.tatami_nummer ?? k.matte_nummer ?? 99).padStart(3,'0')}_0_${k.bracket_name}`,
        urgent: true,
      });
    }

    // Pending Kämpfe (nur die ersten pro Tatami)
    const seenPending = new Set();
    for (const k of data.pending) {
      const tatami = k.tatami_nummer ?? k.matte_nummer;
      const key = `${tatami}_${k.bracket_id}`;
      if (seenPending.has(key)) continue;
      seenPending.add(key);
      list.push({
        type: 'kampf',
        division_code: k.division_code || k.bracket_name,
        label: k.bracket_name,
        tatami,
        time: k.geplante_zeit,
        detail: `Nächster: ${k.k1_vorname ?? '?'} ${k.k1_nachname ?? ''} vs ${k.k2_vorname ?? '?'} ${k.k2_nachname ?? ''}`,
        sortKey: `${String(tatami ?? 99).padStart(3,'0')}_1_${k.bracket_name}`,
        urgent: false,
      });
    }

    // Zeitplan-Slots
    for (const s of (data.zeitplan ?? [])) {
      const status = getSlotStatusSimple(s, turnierdatum);
      if (status === 'done') continue;
      list.push({
        type: 'slot',
        division_code: s.division_code,
        label: s.division_code,
        tatami: s.ring,
        time: s.start_time,
        detail: s.end_time ? `${s.start_time}–${s.end_time} Uhr` : s.start_time ? `${s.start_time} Uhr` : '',
        sortKey: `${String(s.ring ?? 99).padStart(3,'0')}_${s.start_time ?? '99:99'}`,
        status,
        urgent: status === 'active',
      });
    }

    // Deduplizieren nach division_code, sortieren
    const seen = new Set();
    return list
      .filter(it => { if (seen.has(it.division_code)) return false; seen.add(it.division_code); return true; })
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  })();

  // Gruppieren nach Tatami
  const byTatami = {};
  for (const it of items) {
    const key = it.tatami ?? 0;
    if (!byTatami[key]) byTatami[key] = [];
    byTatami[key].push(it);
  }
  const tatamiNrs = Object.keys(byTatami).map(Number).sort((a, b) => a - b);

  if (loading) return (
    <div className="acp-page">
      <div className="acp-loading">Lade Turnierdaten…</div>
    </div>
  );

  return (
    <div className="acp-page">
      <header className="acp-header">
        <div className="acp-header-title">Schreiber-Steuerung</div>
        {data?.turnier && <div className="acp-header-sub">{data.turnier.name}</div>}
      </header>

      {/* ⚠️ Seit dem 16.09.2026 ist diese Seite der AUSNAHMEWEG, nicht der
          Regelfall: Die Ansage-Station (/ansage/:turnierId) ruft von selbst
          auf, sobald ein Ergebnis eingetragen oder eine Wertung gespeichert
          wird. Wer hier von Hand drueckt, ohne das zu wissen, schiebt einen
          zweiten Aufruf auf die Anzeige. */}
      <div className="acp-hinweis">
        Die Aufrufe gehen automatisch raus, sobald ein Ergebnis eingetragen ist.
        Diese Seite ist für den Ausnahmefall — Kategorie von Hand aufrufen oder
        eine freie Durchsage machen.
        <div className="acp-durchsage">
          <input
            type="text"
            value={durchsage}
            maxLength={500}
            placeholder="Freie Durchsage, z. B. „Der Besitzer des blauen Wagens wird am Eingang erwartet.“"
            onChange={(e) => setDurchsage(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') durchsageSenden(); }}
          />
          <button onClick={durchsageSenden} disabled={!durchsage.trim() || durchsageLaeuft}>
            {durchsageLaeuft ? 'wird gesendet…' : 'Durchsagen'}
          </button>
        </div>
        {durchsageMeldung && <div className="acp-durchsage-meldung">{durchsageMeldung}</div>}
      </div>

      <HallenInfo turnierId={turnierId} />

      {sperre && (
        <div className="acp-sperre" role="alert">
          {sperre}
        </div>
      )}

      {activeCall && (
        <div className="acp-active-bar">
          <div className="acp-active-label">
            <span className="acp-active-dot" />
            Aufgerufen: <strong>{activeCall.label || activeCall.division_code}</strong>
            {activeCall.tatami ? ` · Tatami ${activeCall.tatami}` : ''}
          </div>
          <button className="acp-clear-btn" onClick={clearCall}>Aufruf beenden ✕</button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="acp-empty">Keine aufzurufenden Kategorien vorhanden.</div>
      ) : (
        <div className="acp-content">
          {tatamiNrs.map(nr => (
            <div key={nr} className="acp-tatami-group">
              <div className="acp-tatami-title">Tatami {nr || '—'}</div>
              <div className="acp-items">
                {byTatami[nr].map(it => {
                  const isActive = activeCall?.division_code === it.division_code;
                  const isLoading = calling === it.division_code;
                  return (
                    <div key={it.division_code} className={`acp-item${it.urgent ? ' acp-item--urgent' : ''}${isActive ? ' acp-item--active' : ''}`}>
                      <div className="acp-item-info">
                        <div className="acp-item-label">{it.label}</div>
                        {it.detail && <div className="acp-item-detail">{it.detail}</div>}
                      </div>
                      <button
                        className={`acp-call-btn${isActive ? ' acp-call-btn--active' : ''}`}
                        onClick={() => isActive ? clearCall() : call(it.division_code, it.label, it.tatami)}
                        disabled={isLoading}
                      >
                        {isLoading ? '…' : isActive ? '✓ Aktiv' : 'Aufrufen'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
